import { expandTokenGroup } from "@/lib/blog/search-lexical";
import {
  ALEVEL_INTENT_TOKENS,
  BIOLOGY_INTENT_TOKENS,
  BOND_INTENT_TOKENS,
  BURSARY_INTENT_TOKENS,
  CHEMISTRY_INTENT_TOKENS,
  H2_INTENT_TOKENS,
  IP_INTENT_TOKENS,
  JC_INTENT_TOKENS,
  MATHS_INTENT_TOKENS,
  NOTES_INTENT_TOKENS,
  OLEVEL_INTENT_TOKENS,
  PHYSICS_INTENT_TOKENS,
  PRACTICAL_INTENT_TOKENS,
  PRACTICAL_RERANK_SPECIALIST_TOKENS,
  PRACTICAL_SPECIALIST_TOKENS,
  SCHOLARSHIP_INTENT_TOKENS,
  TUITION_INTENT_TOKENS,
} from "@/lib/blog/search-intent";

export type QueryIntent = {
  practical: boolean;
  notes: boolean;
  scholarship: boolean;
  bursary: boolean;
  tuition: boolean;
  bond: boolean;
  h2: boolean;
  oLevel: boolean;
  ip: boolean;
  jc: boolean;
  aLevel: boolean;
  chemistry: boolean;
  biology: boolean;
  physics: boolean;
  maths: boolean;
  broadSubjectPractical: boolean;
};

export type SearchSemanticText = {
  filenameText: string;
  titleText: string;
  descriptionText: string;
  categoryText: string;
  subjectText: string;
  bodyText: string;
};

type ScholarshipEntityId = "gks" | "mext" | "mid-term";
type MatchStrength = "title" | "body" | "none";

export type QueryEntityProfile = {
  queryTokens: ReadonlySet<string>;
  scholarshipEntities: Set<ScholarshipEntityId>;
  hasStrongScholarshipEntity: boolean;
  prefersScholarshipGuideStyle: boolean;
  notesBroadQuery: boolean;
  prefersBursaryGuideStyle: boolean;
  prefersPracticalGuideStyle: boolean;
  wantsBondFree: boolean;
  wantsBondedScholarship: boolean;
};

export type SearchTextSignals = {
  titleHasPracticalSignal: boolean;
  titleHasNotesSignal: boolean;
  titleHasTuitionSignal: boolean;
  titleHasBondSignal: boolean;
  titleHasSyllabusSignal: boolean;
  titleHasH2Signal: boolean;
  titleHasOLevelSignal: boolean;
  titleHasIPSignal: boolean;
  categoryHasPracticalSignal: boolean;
  subjectHasPracticalSignal: boolean;
  bodyHasPracticalSignal: boolean;
  docHasPracticalSignal: boolean;
  categoryHasNotesSignal: boolean;
  subjectHasNotesSignal: boolean;
  bodyHasNotesSignal: boolean;
  docHasNotesSignal: boolean;
  descriptionHasBondSignal: boolean;
  bodyHasBondSignal: boolean;
  docHasBondSignal: boolean;
  subjectHasH2Signal: boolean;
  subjectHasOLevelSignal: boolean;
  subjectHasIPSignal: boolean;
  docHasH2Signal: boolean;
  docHasOLevelSignal: boolean;
  docHasIPSignal: boolean;
  isChemistryDoc: boolean;
  isBiologyDoc: boolean;
  isPhysicsDoc: boolean;
  isMathsDoc: boolean;
};

function hasAnyToken(
  haystack: Set<string>,
  needles: ReadonlySet<string>
): boolean {
  for (const token of needles) {
    if (haystack.has(token)) return true;
  }
  return false;
}

function containsAnyToken(
  haystack: ReadonlySet<string>,
  needles: ReadonlySet<string>
): boolean {
  for (const token of needles) {
    if (haystack.has(token)) return true;
  }
  return false;
}

function hasRawScholarshipSignal(queryTokens: ReadonlySet<string>): boolean {
  return (
    containsAnyToken(queryTokens, SCHOLARSHIP_INTENT_TOKENS) ||
    queryTokens.has("gks") ||
    queryTokens.has("mext") ||
    queryTokens.has("midterm") ||
    (queryTokens.has("mid") && queryTokens.has("term"))
  );
}

function hasRawBursarySignal(queryTokens: ReadonlySet<string>): boolean {
  return containsAnyToken(queryTokens, BURSARY_INTENT_TOKENS);
}

function hasToken(text: string, token: string): boolean {
  return ` ${text} `.includes(` ${token} `);
}

function hasAnyPhrase(text: string, phrases: readonly string[]): boolean {
  for (const phrase of phrases) {
    if (text.includes(phrase)) return true;
  }
  return false;
}

function scholarshipEntityMatchStrength(
  entity: ScholarshipEntityId,
  text: SearchSemanticText
): MatchStrength {
  const titleScope = `${text.titleText} ${text.subjectText}`;
  const bodyScope = `${text.descriptionText} ${text.categoryText} ${text.bodyText}`;
  const entityPhrases: readonly string[] =
    entity === "gks"
      ? ["global korea scholarship"]
      : entity === "mext"
        ? ["monbukagakusho", "japanese government scholarship"]
        : ["mid term", "mid-term"];
  const entityToken =
    entity === "mid-term" ? "midterm" : (entity as "gks" | "mext");

  if (
    hasToken(titleScope, entityToken) ||
    hasAnyPhrase(titleScope, entityPhrases)
  ) {
    return "title";
  }
  if (
    hasToken(bodyScope, entityToken) ||
    hasAnyPhrase(bodyScope, entityPhrases)
  ) {
    return "body";
  }
  return "none";
}

function extractTopicNumber(title: string): number | null {
  const patterns = [/\btopic\s+0*(\d{1,2})\b/];

  for (const pattern of patterns) {
    const match = title.match(pattern);
    if (!match) continue;
    const parsed = Number.parseInt(match[1], 10);
    if (Number.isFinite(parsed)) return parsed;
  }

  const notesStart = title.indexOf("notes");
  if (notesStart !== -1) {
    const notesTail = title.slice(notesStart + "notes".length);
    const noteNumbers = [...notesTail.matchAll(/\b0*(\d{1,2})\b/g)]
      .map((match) => Number.parseInt(match[1], 10))
      .filter((value) => Number.isFinite(value));
    const trailingNoteNumber = noteNumbers.at(-1);
    if (typeof trailingNoteNumber === "number") return trailingNoteNumber;
  }

  const upperSecMatch = title.match(
    /\bupper\s+sec(?:ondary)?(?:\s+year\s+\d{1,2}\s+\d{1,2})?\s+0*(\d{1,2})\b/
  );
  if (upperSecMatch) {
    const parsed = Number.parseInt(upperSecMatch[1], 10);
    if (Number.isFinite(parsed)) return parsed;
  }

  const lowerSecMatch = title.match(
    /\blower\s+sec(?:ondary)?(?:\s+year\s+\d{1,2}\s+\d{1,2})?\s+0*(\d{1,2})\b/
  );
  if (lowerSecMatch) {
    const parsed = Number.parseInt(lowerSecMatch[1], 10);
    if (Number.isFinite(parsed)) return parsed;
  }

  return null;
}

export function inferSearchIntent(queryTerms: readonly string[]): QueryIntent {
  const rawTokens = new Set<string>(queryTerms);
  const tokens = new Set<string>();
  for (const term of queryTerms) {
    for (const expanded of expandTokenGroup(term)) {
      tokens.add(expanded);
    }
  }

  const hasExplicitLevelSignal =
    hasAnyToken(tokens, H2_INTENT_TOKENS) ||
    hasAnyToken(tokens, OLEVEL_INTENT_TOKENS) ||
    hasAnyToken(tokens, IP_INTENT_TOKENS) ||
    hasAnyToken(tokens, ALEVEL_INTENT_TOKENS) ||
    hasAnyToken(tokens, JC_INTENT_TOKENS);
  const hasSubjectSignal =
    hasAnyToken(tokens, CHEMISTRY_INTENT_TOKENS) ||
    hasAnyToken(tokens, BIOLOGY_INTENT_TOKENS) ||
    hasAnyToken(tokens, PHYSICS_INTENT_TOKENS) ||
    hasAnyToken(tokens, MATHS_INTENT_TOKENS);
  const practicalSignal =
    hasAnyToken(tokens, PRACTICAL_INTENT_TOKENS) ||
    hasAnyToken(tokens, new Set(["experimnt", "expermnt"]));

  return {
    practical: practicalSignal,
    notes: hasAnyToken(tokens, NOTES_INTENT_TOKENS),
    scholarship: hasRawScholarshipSignal(rawTokens),
    bursary: hasRawBursarySignal(rawTokens),
    tuition: hasAnyToken(tokens, TUITION_INTENT_TOKENS),
    bond: hasAnyToken(tokens, BOND_INTENT_TOKENS),
    h2: hasAnyToken(tokens, H2_INTENT_TOKENS),
    oLevel: hasAnyToken(tokens, OLEVEL_INTENT_TOKENS),
    ip: hasAnyToken(tokens, IP_INTENT_TOKENS),
    jc: hasAnyToken(tokens, JC_INTENT_TOKENS),
    aLevel: hasAnyToken(tokens, ALEVEL_INTENT_TOKENS),
    chemistry: hasAnyToken(tokens, CHEMISTRY_INTENT_TOKENS),
    biology: hasAnyToken(tokens, BIOLOGY_INTENT_TOKENS),
    physics: hasAnyToken(tokens, PHYSICS_INTENT_TOKENS),
    maths: hasAnyToken(tokens, MATHS_INTENT_TOKENS),
    broadSubjectPractical:
      practicalSignal && !hasExplicitLevelSignal && hasSubjectSignal,
  };
}

export function buildSearchQueryEntityProfile({
  normalizedQuery,
  queryTerms,
  intent,
}: {
  normalizedQuery: string;
  queryTerms: readonly string[];
  intent: QueryIntent;
}): QueryEntityProfile {
  const queryTokenSet = new Set(queryTerms);
  const scholarshipEntities = new Set<ScholarshipEntityId>();

  if (
    queryTokenSet.has("gks") ||
    hasAnyPhrase(normalizedQuery, ["global korea scholarship"])
  ) {
    scholarshipEntities.add("gks");
  }
  if (
    queryTokenSet.has("mext") ||
    hasAnyPhrase(normalizedQuery, [
      "monbukagakusho",
      "japanese government scholarship",
    ])
  ) {
    scholarshipEntities.add("mext");
  }
  if (
    queryTokenSet.has("midterm") ||
    (queryTokenSet.has("mid") && queryTokenSet.has("term")) ||
    hasAnyPhrase(normalizedQuery, ["mid term", "mid-term"])
  ) {
    scholarshipEntities.add("mid-term");
  }

  const hasStrongScholarshipEntity = scholarshipEntities.size > 0;
  const prefersScholarshipGuideStyle =
    intent.scholarship &&
    !queryTokenSet.has("profile") &&
    !queryTokenSet.has("interview");
  const notesBroadQuery =
    intent.notes &&
    !queryTokenSet.has("topic") &&
    !queryTokenSet.has("formulae") &&
    !queryTokenSet.has("formula") &&
    !queryTokenSet.has("paper");
  const wantsBondFree =
    queryTokenSet.has("free") ||
    hasAnyPhrase(normalizedQuery, ["bond free", "bond-free"]);
  const wantsBondedScholarship = intent.bond && !wantsBondFree;
  const prefersBursaryGuideStyle =
    intent.bursary &&
    !queryTokenSet.has("profile") &&
    !queryTokenSet.has("application") &&
    !queryTokenSet.has("deadline");
  const prefersPracticalGuideStyle =
    intent.practical &&
    (intent.h2 ||
      intent.jc ||
      intent.chemistry ||
      intent.physics ||
      intent.biology ||
      intent.broadSubjectPractical) &&
    !containsAnyToken(queryTokenSet, PRACTICAL_SPECIALIST_TOKENS);

  return {
    queryTokens: queryTokenSet,
    scholarshipEntities,
    hasStrongScholarshipEntity,
    prefersScholarshipGuideStyle,
    notesBroadQuery,
    prefersBursaryGuideStyle,
    prefersPracticalGuideStyle,
    wantsBondFree,
    wantsBondedScholarship,
  };
}

export function buildSearchTextSignals(
  text: SearchSemanticText
): SearchTextSignals {
  const docSubject = text.subjectText;
  const docCategory = text.categoryText;
  const filename = text.filenameText;
  const title = text.titleText;
  const description = text.descriptionText;
  const body = text.bodyText;

  const titleHasPracticalSignal =
    title.includes("practical") ||
    title.includes("experiment") ||
    title.includes("lab") ||
    title.includes("investigation");
  const filenameHasPracticalSignal =
    filename.includes("practical") ||
    filename.includes("experiment") ||
    filename.includes("lab") ||
    filename.includes("investigation");
  const titleHasNotesSignal = title.includes("notes");
  const titleHasTuitionSignal = title.includes("tuition");
  const titleHasBondSignal = title.includes("bond");
  const titleHasSyllabusSignal = title.includes("syllabus");
  const titleHasH2Signal = title.includes("h2");
  const titleHasOLevelSignal =
    title.includes("o level") || title.includes("olevel");
  const titleHasIPSignal =
    title.includes("integrated programme") ||
    title.startsWith("ip ") ||
    title.includes(" ip ");
  const categoryHasPracticalSignal =
    docCategory.includes("practical") || docCategory.includes("experiment");
  const subjectHasPracticalSignal =
    docSubject.includes("practical") || docSubject.includes("experiments");
  const bodyHasPracticalSignal =
    body.includes("practical") ||
    body.includes("experiment") ||
    body.includes("lab");
  const docHasPracticalSignal =
    titleHasPracticalSignal ||
    filenameHasPracticalSignal ||
    categoryHasPracticalSignal ||
    subjectHasPracticalSignal ||
    bodyHasPracticalSignal;
  const categoryHasNotesSignal = docCategory.includes("notes");
  const subjectHasNotesSignal = docSubject.includes("notes");
  const bodyHasNotesSignal = body.includes("notes");
  const docHasNotesSignal =
    titleHasNotesSignal ||
    categoryHasNotesSignal ||
    subjectHasNotesSignal ||
    bodyHasNotesSignal;
  const descriptionHasBondSignal = description.includes("bond");
  const bodyHasBondSignal = body.includes("bond");
  const docHasBondSignal =
    titleHasBondSignal || descriptionHasBondSignal || bodyHasBondSignal;

  const subjectHasH2Signal = docSubject.includes("h2");
  const subjectHasOLevelSignal = docSubject.includes("olevel");
  const subjectHasIPSignal =
    docSubject.startsWith("ip ") || docSubject.includes(" ip ");
  const docHasH2Signal = subjectHasH2Signal || titleHasH2Signal;
  const docHasOLevelSignal = subjectHasOLevelSignal || titleHasOLevelSignal;
  const docHasIPSignal = subjectHasIPSignal || titleHasIPSignal;
  const isChemistryDoc =
    title.includes("chemistry") || docSubject.includes("chemistry");
  const isBiologyDoc =
    title.includes("biology") || docSubject.includes("biology");
  const isPhysicsDoc =
    title.includes("physics") || docSubject.includes("physics");
  const isMathsDoc =
    title.includes("maths") ||
    title.includes("math") ||
    docSubject.includes("math");

  return {
    titleHasPracticalSignal,
    titleHasNotesSignal,
    titleHasTuitionSignal,
    titleHasBondSignal,
    titleHasSyllabusSignal,
    titleHasH2Signal,
    titleHasOLevelSignal,
    titleHasIPSignal,
    categoryHasPracticalSignal,
    subjectHasPracticalSignal,
    bodyHasPracticalSignal,
    docHasPracticalSignal,
    categoryHasNotesSignal,
    subjectHasNotesSignal,
    bodyHasNotesSignal,
    docHasNotesSignal,
    descriptionHasBondSignal,
    bodyHasBondSignal,
    docHasBondSignal,
    subjectHasH2Signal,
    subjectHasOLevelSignal,
    subjectHasIPSignal,
    docHasH2Signal,
    docHasOLevelSignal,
    docHasIPSignal,
    isChemistryDoc,
    isBiologyDoc,
    isPhysicsDoc,
    isMathsDoc,
  };
}

export function shouldApplyStrictIntentGate(intent: QueryIntent): boolean {
  if (!intent.practical) return false;
  return (
    intent.ip ||
    intent.h2 ||
    intent.oLevel ||
    intent.aLevel ||
    intent.jc ||
    intent.chemistry ||
    intent.biology ||
    intent.physics ||
    intent.maths
  );
}

export function isH2PhysicsPracticalSupportText(
  intent: QueryIntent,
  signals: SearchTextSignals,
  text: SearchSemanticText
): boolean {
  if (!intent.practical) return false;
  if (!(intent.h2 || intent.aLevel || intent.jc)) return false;
  if (intent.ip || intent.oLevel || !intent.physics) return false;
  if (!signals.isPhysicsDoc || !signals.docHasH2Signal) return false;

  const title = text.titleText;
  if (title.includes("data booklet") || title.includes("data and formulae")) {
    return false;
  }
  const hasFormulaCompanionSignal =
    title.includes("formulae") ||
    title.includes("formula sheet") ||
    title.includes("definitions");
  const hasRevisionCompanionFormat =
    title.includes("guide") ||
    title.includes("notes") ||
    title.includes("pdf") ||
    title.includes("sheet");

  return hasFormulaCompanionSignal && hasRevisionCompanionFormat;
}

export function passesSearchStrictIntentGate({
  intent,
  signals,
  text,
}: {
  intent: QueryIntent;
  signals: SearchTextSignals;
  text: SearchSemanticText;
}): boolean {
  if (!shouldApplyStrictIntentGate(intent)) return true;
  if (!signals.docHasPracticalSignal) {
    return isH2PhysicsPracticalSupportText(intent, signals, text);
  }
  if (intent.ip && !signals.docHasIPSignal) return false;
  if ((intent.h2 || intent.aLevel || intent.jc) && !signals.docHasH2Signal) {
    return false;
  }
  if (intent.oLevel && !signals.docHasOLevelSignal) return false;
  if (intent.chemistry && !signals.isChemistryDoc) return false;
  if (intent.biology && !signals.isBiologyDoc) return false;
  if (intent.physics && !signals.isPhysicsDoc) return false;
  if (intent.maths && !signals.isMathsDoc) return false;
  if (
    !intent.tuition &&
    signals.titleHasTuitionSignal &&
    !signals.titleHasPracticalSignal
  ) {
    return false;
  }
  return true;
}

export function scoreSearchIntentAlignment({
  intent,
  signals,
  text,
}: {
  intent: QueryIntent;
  signals: SearchTextSignals;
  text: SearchSemanticText;
}): number {
  let score = 0;
  const docSubject = text.subjectText;
  const docCategory = text.categoryText;
  const title = text.titleText;
  const {
    titleHasPracticalSignal,
    titleHasNotesSignal,
    titleHasTuitionSignal,
    titleHasBondSignal,
    titleHasSyllabusSignal,
    titleHasH2Signal,
    titleHasOLevelSignal,
    titleHasIPSignal,
    categoryHasPracticalSignal,
    docHasPracticalSignal,
    categoryHasNotesSignal,
    subjectHasNotesSignal,
    docHasNotesSignal,
    descriptionHasBondSignal,
    bodyHasBondSignal,
    docHasBondSignal,
    subjectHasH2Signal,
    subjectHasOLevelSignal,
    subjectHasIPSignal,
    docHasH2Signal,
    isChemistryDoc,
    isBiologyDoc,
    isPhysicsDoc,
    isMathsDoc,
  } = signals;

  if (intent.practical) {
    if (docSubject.includes("experiments")) score += 30;
    if (docSubject.includes("practical")) score += 24;
    if (categoryHasPracticalSignal) score += 14;
    if (titleHasPracticalSignal) score += 18;
    if (isH2PhysicsPracticalSupportText(intent, signals, text)) {
      score += 180;
    }
    if (!docHasPracticalSignal) {
      if (isH2PhysicsPracticalSupportText(intent, signals, text)) {
        score += 24;
      } else {
        score -= 16;
      }
    }
    if (titleHasTuitionSignal && !titleHasPracticalSignal) score -= 10;
    if (
      titleHasTuitionSignal &&
      (intent.h2 || intent.oLevel) &&
      !titleHasH2Signal &&
      !titleHasOLevelSignal
    ) {
      score -= 10;
    }
    if (intent.oLevel && !subjectHasOLevelSignal && !titleHasOLevelSignal) {
      score -= 8;
    }
    if (
      (intent.h2 || intent.aLevel || intent.jc) &&
      !subjectHasH2Signal &&
      !titleHasH2Signal
    ) {
      score -= 8;
    }
    if (intent.broadSubjectPractical && !docHasH2Signal) {
      score -= 6;
    }
    if (intent.broadSubjectPractical && docHasH2Signal) {
      score += 8;
    }
    if (intent.chemistry && !isChemistryDoc) score -= 8;
    if (intent.biology && !isBiologyDoc) score -= 8;
    if (intent.physics && !isPhysicsDoc) score -= 8;
    if (intent.maths && !isMathsDoc) score -= 8;
    // Penalise combined-science pages for single-subject practical queries.
    // "o level chemistry practical" should prefer the dedicated chemistry guide
    // over a combined-science page that covers the subject as one of several.
    const querySingleSubjectCount =
      (intent.chemistry ? 1 : 0) +
      (intent.biology ? 1 : 0) +
      (intent.physics ? 1 : 0);
    if (querySingleSubjectCount === 1 && title.includes("combined")) {
      score -= 40;
    }
  }

  if (intent.notes) {
    if (subjectHasNotesSignal) score += 24;
    if (categoryHasNotesSignal) score += 14;
    if (titleHasNotesSignal) score += 12;
    if (!docHasNotesSignal) score -= 10;
    if (titleHasSyllabusSignal && !titleHasNotesSignal) score -= 16;
    if (titleHasTuitionSignal && !titleHasNotesSignal) score -= 8;
    if (intent.ip && !subjectHasIPSignal && !titleHasIPSignal) score -= 8;
    if (
      (intent.h2 || intent.aLevel || intent.jc) &&
      !subjectHasH2Signal &&
      !titleHasH2Signal
    ) {
      score -= 8;
    }
    if (intent.chemistry && !isChemistryDoc) score -= 8;
    if (intent.biology && !isBiologyDoc) score -= 8;
    if (intent.physics && !isPhysicsDoc) score -= 8;
    if (intent.maths && !isMathsDoc) score -= 8;
  }

  if (intent.scholarship) {
    if (docSubject === "scholarships") score += 30;
    if (docCategory.includes("scholarship")) score += 14;
    if (title.includes("scholarship")) score += 12;
    if (intent.bond && !docHasBondSignal) score -= 14;
    if (docSubject === "bursaries" && !intent.bursary) score -= 12;
  }

  if (intent.bursary) {
    if (docSubject === "bursaries") score += 30;
    if (docCategory.includes("bursary")) score += 14;
    if (title.includes("bursary")) score += 12;
  }

  if (intent.tuition) {
    if (docSubject.includes("tuition")) score += 24;
    if (titleHasTuitionSignal) score += 16;
    // Practical lab/experiment pages are not relevant for tuition queries.
    // Penalise them so syllabus and tuition-guide pages hold their recall slots.
    if (signals.docHasPracticalSignal && !titleHasTuitionSignal) {
      score -= 14;
    }
  }

  if (intent.bond) {
    if (titleHasBondSignal) score += 24;
    if (descriptionHasBondSignal) score += 12;
    if (bodyHasBondSignal) score += 10;
    if (!docHasBondSignal) score -= 20;
  }

  if (intent.h2 || intent.aLevel) {
    if (subjectHasH2Signal) score += 18;
    if (titleHasH2Signal) score += 14;
    if (subjectHasOLevelSignal || titleHasOLevelSignal) score -= 8;
  }

  if (intent.oLevel) {
    if (subjectHasOLevelSignal) score += 18;
    if (titleHasOLevelSignal) score += 14;
    if (subjectHasH2Signal || titleHasH2Signal) score -= 8;
    if (!subjectHasOLevelSignal && !titleHasOLevelSignal) score -= 10;
  }

  if (intent.ip) {
    if (subjectHasIPSignal) score += 16;
    if (titleHasIPSignal) score += 10;
  }

  if (intent.jc && (subjectHasH2Signal || titleHasH2Signal)) {
    score += 8;
  }

  if (intent.chemistry && isChemistryDoc) score += 12;
  if (intent.chemistry && !isChemistryDoc) score -= 7;
  if (intent.biology && isBiologyDoc) score += 12;
  if (intent.biology && !isBiologyDoc) score -= 7;
  if (intent.physics && isPhysicsDoc) score += 12;
  if (intent.physics && !isPhysicsDoc) score -= 7;
  if (intent.maths && isMathsDoc) score += 12;
  if (intent.maths && !isMathsDoc) score -= 7;

  return score;
}

export function scoreSearchNamedEntityPrecision({
  intent,
  queryEntities,
  signals,
  text,
}: {
  intent: QueryIntent;
  queryEntities: QueryEntityProfile;
  signals: SearchTextSignals;
  text: SearchSemanticText;
}): number {
  let score = 0;
  const title = text.titleText;
  const queryTokens = queryEntities.queryTokens;

  if (intent.scholarship) {
    if (queryEntities.prefersScholarshipGuideStyle) {
      if (title.includes("guide")) score += 12;
      if (title.includes("pathways")) score += 14;
      if (title.includes("playbook")) score += 14;
      if (title.includes("profile")) score -= 16;
      if (title.includes("interview")) score -= 16;
      if (title.includes("compare")) score -= 10;
      if (title.includes("workflow")) score -= 8;
      if (title.includes("decision guide")) score -= 12;
      if (title.includes("lifetime earnings")) score -= 10;
      if (title.includes(" vs ") || title.includes(" versus ")) score -= 10;
    }

    if (queryEntities.wantsBondedScholarship) {
      if (title.includes("bonded")) score += 16;
      if (title.includes("bonded scholarship")) score += 22;
      if (title.includes("bond free") || title.includes("bond-free")) {
        score -= 28;
      }
      if (!title.includes("bond")) score -= 34;
      if (
        title.includes("breaking") ||
        title.includes("what happens") ||
        title.includes("transition")
      ) {
        score -= 60;
      }
    }

    if (queryEntities.wantsBondFree) {
      if (title.includes("bond free") || title.includes("bond-free")) score += 26;
      if (title.includes("bond free scholarship")) score += 14;
      if (
        title.includes("breaking") ||
        title.includes("what happens") ||
        title.includes("transition")
      ) {
        score -= 48;
      }
    }

    if (
      queryEntities.prefersScholarshipGuideStyle &&
      !queryTokens.has("breaking") &&
      !queryTokens.has("break") &&
      !queryTokens.has("happens")
    ) {
      if (title.includes("breaking")) score -= 36;
      if (title.includes("what happens")) score -= 36;
      if (title.includes("career transition")) score -= 24;
    }
  }

  if (intent.scholarship && queryEntities.hasStrongScholarshipEntity) {
    const singleRequestedEntity =
      queryEntities.scholarshipEntities.size === 1
        ? [...queryEntities.scholarshipEntities][0]
        : null;

    for (const entity of ["gks", "mext", "mid-term"] as const) {
      const strength = scholarshipEntityMatchStrength(entity, text);
      const requested = queryEntities.scholarshipEntities.has(entity);

      if (requested) {
        if (strength === "title") score += 26;
        else if (strength === "body") score += 6;
        else score -= 12;
      } else if (
        queryEntities.scholarshipEntities.size === 1 &&
        strength !== "none"
      ) {
        score += strength === "title" ? -18 : -10;
      }
    }

    if (singleRequestedEntity === "mid-term" && title.includes("conversion")) {
      score += 18;
    }
    if (singleRequestedEntity === "gks" && title.includes("embassy")) {
      score += 16;
    }
    if (singleRequestedEntity === "mext" && title.includes("embassy")) {
      score += 16;
    }

    if (
      queryEntities.prefersScholarshipGuideStyle &&
      (singleRequestedEntity === "gks" || singleRequestedEntity === "mext") &&
      queryTokens.has("singapore")
    ) {
      if (title.includes("embassy")) score += 10;
      if (title.includes("interview")) score -= 12;
    }

    if (
      singleRequestedEntity !== null &&
      queryEntities.prefersScholarshipGuideStyle &&
      !queryTokens.has("interview")
    ) {
      if (title.includes("interview")) score -= 16;
      if (title.includes("profile")) score -= 10;
    }

    if (singleRequestedEntity === "mid-term") {
      if (title.includes("guide")) score += 12;
      if (title.includes("conversion")) score += 18;
      if (title.includes("mid-term") && title.includes("guide")) score += 18;
      if (title.includes("conversion guide")) score += 28;
      if (title.includes("profile")) score -= 40;
    }
  }

  if (intent.notes && queryEntities.notesBroadQuery) {
    if (title.includes("overview")) score += 12;

    const topicNumber = extractTopicNumber(title);
    if (topicNumber === 1) score += 28;
    else if (topicNumber === 2) score += 24;
    else if (topicNumber === 3) score += 18;
    else if (topicNumber !== null && topicNumber <= 5) score += 10;
    else if (topicNumber !== null && topicNumber <= 8) score += 4;
    else if (topicNumber !== null) {
      score -= Math.min(12, topicNumber - 8);
    }

    if (
      !signals.docHasNotesSignal &&
      (title.includes("paper") ||
        title.includes("format") ||
        title.includes("syllabus"))
    ) {
      score -= 8;
    }
  }

  if (intent.bursary && queryEntities.prefersBursaryGuideStyle) {
    if (title.includes("guide")) score += 18;
    if (title.includes("map")) score += 26;
    if (title.includes("grants")) score += 12;
    if (title.includes("profile")) score -= 14;
    if (queryTokens.has("singapore") && title.includes("profile")) score -= 12;

    if (queryTokens.has("singapore")) {
      if (title.includes("map")) score += 12;
      if (title.includes("financial aid")) score += 10;
      if (title.includes("primary to university")) score += 10;
      if (title.includes("grants")) score += 6;
      if (title.includes("ite bursary")) score -= 18;
      if (title.includes("university bursary")) score -= 12;
      if (title.includes("application guide")) score -= 8;
    }

    if (queryTokens.has("moe") && queryTokens.has("fas")) {
      if (title.includes("guide")) score += 18;
      if (title.includes("financial assistance")) score += 18;
      if (title.includes("profile")) score -= 12;
      // Strongly boost the FAS-specific guide: "fas" in title is a near-unique
      // signal for the MOE Financial Assistance Scheme page.
      if (title.includes("fas")) score += 22;
      // Penalise generic bursary guides that don't specifically cover FAS.
      if (!title.includes("financial assistance") && !title.includes("fas")) {
        score -= 18;
      }
    }
  }

  if (intent.practical && queryEntities.prefersPracticalGuideStyle) {
    const titleSpecialistTokens = PRACTICAL_RERANK_SPECIALIST_TOKENS.filter(
      (token) => hasToken(title, token)
    );
    const requestedSpecialistTokens = PRACTICAL_RERANK_SPECIALIST_TOKENS.filter(
      (token) => queryTokens.has(token)
    );

    const isBroadH2PracticalQuery =
      (intent.h2 || intent.broadSubjectPractical) &&
      !intent.ip &&
      !intent.oLevel;

    // Broad O-Level practical query: no H2/JC/IP level specified.
    // Mirrors isBroadH2PracticalQuery for O-Level contexts.
    const isBroadOLevelPracticalQuery =
      intent.oLevel && !intent.h2 && !intent.ip;

    if (title.includes("guide")) score += 12;
    if (title.includes("practical guide")) score += 24;
    const hasTitlePracticalSignal =
      title.includes("practical") ||
      title.includes("guide") ||
      (intent.broadSubjectPractical && title.includes("lab readiness"));
    if (title.includes("practical")) score += 10;
    if (!hasTitlePracticalSignal) {
      score -= signals.docHasPracticalSignal ? 10 : 28;
    }
    if (title.includes("playbook")) score += 12;
    if (title.includes("toolkit")) score += 12;
    if (title.includes("manual")) score += 10;
    if (title.includes("handbook")) score += 10;
    if (title.includes("lab readiness")) score += 16;
    if (title.includes("mastery")) score += 10;
    if (title.includes("deep dive")) score -= 8;
    if (title.includes("masterclass")) score -= 20;
    if (title.includes("framework")) score -= 10;
    if (title.includes("workflow")) score -= 12;
    if (title.includes("olympiad")) score -= 24;
    if (intent.chemistry && title.includes("volumetric")) score -= 10;
    if (intent.chemistry && title.includes("kinetics")) score -= 6;
    if (intent.chemistry && title.includes("organic synthesis")) score -= 10;
    // Restrict calorimetry boost to H2 / broad-subject contexts. O-Level
    // chemistry practical queries should not boost O-Level calorimetry pages
    // ahead of the dedicated O-Level practical overview guide.
    if (
      intent.chemistry &&
      !intent.oLevel &&
      title.includes("calorimetry")
    )
      score += 16;
    if (intent.chemistry && title.includes("pdo")) score -= 14;
    if (intent.chemistry && title.includes("uncertainty")) score -= 12;
    if (intent.chemistry && !title.includes("guide")) score -= 10;

    // Penalise single-experiment "how-to" and named-experiment pages for broad
    // practical queries. These pages are relevant only when a student queries
    // for a specific technique; they should not compete with the comprehensive
    // overview guide on queries like "h2 chemistry practical" or
    // "o level physics practical".
    if (isBroadH2PracticalQuery || isBroadOLevelPracticalQuery) {
      if (title.includes("how to")) score -= 30;
      if (title.includes("experiment")) score -= 20;
    }

    if (isBroadH2PracticalQuery) {
      if (title.includes("practical guide")) score += 10;
      if (intent.physics && title.includes("paper 4")) score += 20;
      if (intent.physics && title.includes("planning")) score += 14;
      if (intent.physics && title.includes("uncertainty")) score += 14;
      if (intent.physics && title.includes("experiments")) score += 12;
      if (intent.physics && title.includes("formulae")) score += 8;
      if (!hasTitlePracticalSignal && signals.docHasPracticalSignal) {
        score -= 4;
      } else if (!hasTitlePracticalSignal) {
        score -= 12;
      }
      if (title.includes("masterclass")) score -= 12;
      if (title.includes("deep dive")) score -= 6;
      if (intent.chemistry && title.includes("calorimetry")) score += 14;
      if (intent.chemistry && title.includes("pdo")) score -= 8;
      if (intent.chemistry && title.includes("uncertainty")) score -= 6;
    }

    if (isBroadOLevelPracticalQuery) {
      if (title.includes("practical guide")) score += 8;
      if (title.includes("playbook")) score += 16;
      if (title.includes("toolkit")) score += 16;
      if (title.includes("paper 3")) score += 8;
      if (title.includes("masterclass")) score -= 12;
      if (title.includes("deep dive")) score -= 6;
      if (
        intent.chemistry &&
        title.includes("paper 3") &&
        (title.includes("planning") || title.includes("scenario"))
      ) {
        score += 40;
      }
      if (intent.physics && title.includes("paper 3")) score += 12;
      if (intent.physics && title.includes("toolkit")) score += 12;
      if (intent.physics && title.includes("playbook")) score += 12;
      if (intent.chemistry && queryTokens.has("titration") && title.includes("playbook")) {
        score += 22;
      }
      // Boost the broad O-Level practical overview guide: has practical+guide
      // without a specialist focus - the most useful resource for the query.
      if (
        title.includes("practical") &&
        title.includes("guide") &&
        titleSpecialistTokens.length === 0 &&
        !title.includes("combined")
      ) {
        score += 24;
      }
      // Combined-science pages cover multiple subjects; demote them additionally
      // when the user queried for a single subject.
      const querySingleSubjectOLevel =
        (intent.chemistry ? 1 : 0) +
        (intent.biology ? 1 : 0) +
        (intent.physics ? 1 : 0);
      if (querySingleSubjectOLevel === 1 && title.includes("combined")) {
        score -= 28;
      }
    }

    if (intent.broadSubjectPractical && intent.chemistry) {
      if (signals.docHasH2Signal) score += 12;
      if (signals.docHasOLevelSignal && title.includes("practical guide")) score += 24;
      else if (signals.docHasOLevelSignal) score -= 4;
      if (signals.docHasOLevelSignal && title.includes("paper 3")) score += 18;
      if (signals.docHasOLevelSignal && title.includes("planning")) score += 10;
      if (signals.docHasOLevelSignal && title.includes("qa")) score += 8;
      if (signals.docHasOLevelSignal && title.includes("notes")) score += 6;
      if (title.includes("lab readiness")) score += 18;
      if (title.includes("checklist")) score += 14;
      if (title.includes("combined")) score -= 20;
      if (title.includes("calorimetry")) score += 10;
    }

    if (
      requestedSpecialistTokens.length === 0 &&
      titleSpecialistTokens.length > 0
    ) {
      if (intent.chemistry && title.includes("calorimetry")) {
        score += 10;
      } else {
        score -= Math.min(24, titleSpecialistTokens.length * 8);
        if (isBroadH2PracticalQuery) {
          score -= Math.min(10, titleSpecialistTokens.length * 5);
          if (intent.chemistry && title.includes("calorimetry")) {
            score += 4;
          }
        }
        if (isBroadOLevelPracticalQuery) {
          score -= Math.min(22, titleSpecialistTokens.length * 11);
        }
      }
    } else if (requestedSpecialistTokens.length > 0) {
      const matchedRequestedSpecialists = requestedSpecialistTokens.filter(
        (token) => hasToken(title, token)
      ).length;
      const offTargetSpecialists = titleSpecialistTokens.filter(
        (token) => !queryTokens.has(token)
      ).length;

      if (matchedRequestedSpecialists > 0) {
        score += matchedRequestedSpecialists * 18;
      } else {
        score -= 12;
      }

      if (offTargetSpecialists > 0) {
        score -= Math.min(18, offTargetSpecialists * 6);
      }
    }
    // Penalise private-candidate admin/requirement pages for general practical
    // queries. These pages cover exam registration logistics, not practical
    // technique, and should not compete with dedicated practical guides unless
    // the user explicitly queried for declaration, certification, or
    // private-candidate info.
    if (
      !queryTokens.has("private") &&
      !queryTokens.has("certif") &&
      !queryTokens.has("certification") &&
      !queryTokens.has("declaration") &&
      !queryTokens.has("declare") &&
      !queryTokens.has("candidate") &&
      !queryTokens.has("candidates")
    ) {
      if (
        title.includes("private candidate") ||
        title.includes("private candidates") ||
        title.includes("private-candidate") ||
        title.includes("private-candidates") ||
        title.includes("private candidate declaration") ||
        title.includes("practical declaration") ||
        title.includes("private candidate certification") ||
        title.includes("certification")
      ) {
        score -= 120;
      }
    }
  }

  if (intent.tuition) {
    if (title.includes("guide")) score += 8;
    if (title.includes("syllabus")) score += 18;
    if (title.includes("group vs one to one")) score += 24;
    if (title.includes("ip student guide")) score += 18;
    if (title.includes("parent and student guide")) score -= 8;
    if (
      intent.ip &&
      intent.physics &&
      title.includes("a level") &&
      title.includes("physics") &&
      title.includes("syllabus")
    ) {
      score += 24;
    }
  }

  return score;
}

export function evaluateSearchSemanticText({
  intent,
  queryEntities,
  text,
}: {
  intent: QueryIntent;
  queryEntities: QueryEntityProfile;
  text: SearchSemanticText;
}): {
  intentBoost: number;
  entityBoost: number;
  signals: SearchTextSignals;
  passesIntentGate: boolean;
  isH2PhysicsPracticalSupportText: boolean;
} {
  const signals = buildSearchTextSignals(text);
  return {
    intentBoost: scoreSearchIntentAlignment({
      intent,
      signals,
      text,
    }),
    entityBoost: scoreSearchNamedEntityPrecision({
      intent,
      queryEntities,
      signals,
      text,
    }),
    signals,
    passesIntentGate: passesSearchStrictIntentGate({
      intent,
      signals,
      text,
    }),
    isH2PhysicsPracticalSupportText: isH2PhysicsPracticalSupportText(
      intent,
      signals,
      text
    ),
  };
}
